import { useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { premergePlayers } from '../../../../api/index.js';

const DEFAULT_RETURN_TO = '/admin/environment-export';

const getSafeReturnTo = (value) => {
  if (!value || !value.startsWith('/') || value.startsWith('//')) {
    return DEFAULT_RETURN_TO;
  }

  return value;
};

const EnvironmentExportMergeLauncher = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const startedRef = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Evite un double appel à premergePlayers en React StrictMode.
    if (startedRef.current) return;
    startedRef.current = true;

    const params = new URLSearchParams(location.search);
    const idPlayer = params.get('keep');
    const idsToDelete = params
      .getAll('delete')
      .filter(Boolean)
      .filter((id) => id !== idPlayer);
    const returnTo = getSafeReturnTo(params.get('returnTo'));

    if (!idPlayer || idsToDelete.length === 0) {
      setError('Paramètres de fusion incomplets : choisissez un joueur à conserver et au moins un joueur à supprimer.');
      return;
    }

    const prepareMerge = async () => {
      try {
        const mergedResponse = await premergePlayers(idPlayer, idsToDelete);

        if (mergedResponse.status !== 200) {
          throw new Error(`Préparation de la fusion impossible (HTTP ${mergedResponse.status}).`);
        }

        const {
          mergedPlayer,
          idsToDelete: preparedIdsToDelete,
          matchesUpdated,
          gamefactsUpdated,
        } = mergedResponse.data;

        // Même contrat location.state que le mode Fusion historique.
        navigate('/sports/players/merge', {
          replace: true,
          state: {
            mergedPlayer,
            idsToDelete: preparedIdsToDelete,
            matchesUpdated,
            gamefactsUpdated,
            returnTo,
          },
        });
      } catch (e) {
        console.error('Failed to prepare player merge from environment export:', e);
        setError(e?.response?.data?.message || e?.message || 'Impossible de préparer la fusion des joueurs.');
      }
    };

    prepareMerge();
  }, [location.search, navigate]);

  return (
    <Box className="centered-medium-wrapper" sx={{ py: 4 }}>
      {error ? (
        <Alert severity="error" sx={{ width: '100%', maxWidth: 720 }}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">Impossible d’ouvrir la fusion</Typography>
            <Typography variant="body2">{error}</Typography>
          </Stack>
        </Alert>
      ) : (
        <Stack alignItems="center" spacing={1.5}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            Préparation de la fusion des joueurs…
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default EnvironmentExportMergeLauncher;
